import type { UserFrontend } from './types'
import { getDoc, setDoc, getDocs } from 'firebase/firestore'
import { StorageModuleDeps } from '~common/storage/types'
import { docRef, fillDoc, collectionRef } from '~common/utils/firestore-helpers'

export class AuthStorageModule {
    constructor(private readonly deps: StorageModuleDeps) {}

    async getCurrentUser(): Promise<UserFrontend | null> {
        const userId = this.deps.getCurrentUserId()
        if (!userId) {
            return null
        }
        return this.getUser(userId)
    }

    async getUser(userId: string): Promise<UserFrontend | null> {
        const userRef = docRef<UserFrontend>(this.deps.db, 'users', userId)
        const userDoc = await getDoc(userRef)
        return userDoc.exists() ? fillDoc(userDoc) : null
    }

    async updateUser(
        userId: string,
        userData: Partial<
            Pick<
                UserFrontend,
                'name' | 'picture' | 'googleDriveFolderHierarchy' | 'settings'
            >
        >,
    ): Promise<void> {
        const userRef = docRef<UserFrontend>(this.deps.db, 'users', userId)
        await setDoc(userRef, userData, { merge: true })
    }

    async hasGoogleAccounts(userId: string): Promise<boolean> {
        const googleAccountsRef = collectionRef(
            this.deps.db,
            'users',
            userId,
            'googleAccounts',
        )
        const snapshot = await getDocs(googleAccountsRef)
        return !snapshot.empty
    }

    async getAllGoogleAccountEmails(userId: string): Promise<string[]> {
        const googleAccountsRef = collectionRef(
            this.deps.db,
            'users',
            userId,
            'googleAccounts',
        )
        const snapshot = await getDocs(googleAccountsRef)
        return snapshot.docs.map((doc) => doc.id)
    }
}
